import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getManufacturerCatalogue,
  type ManufacturerCatalogueEntry,
} from '../services/firebase';
import { subscribeManufacturerCatalogue } from '../services/manufacturerCatalogueOverlay';

/**
 * The manufacturer catalogue, kept current when one is created or deleted
 * elsewhere in the app.
 *
 * Nine surfaces held their own `manufacturers` state and their own load effect,
 * most of them gated on `manufacturers.length > 0` — a guard that is right about
 * cost (the catalogue is one Algolia request serving dropdowns and id → name
 * maps) and wrong about freshness: it means a surface loads the catalogue once
 * and then never looks again, so a manufacturer created in the quick-create
 * modal was missing from every one of them until the page was reloaded.
 *
 * This hook keeps the cheap part — one fetch, not one per open — and adds the
 * subscription that makes it correct. On a create or delete, every mounted
 * consumer refetches; the overlay in `manufacturerCatalogueOverlay.ts` means
 * that refetch is right immediately rather than a second or two later when the
 * Algolia trigger catches up.
 *
 * `enabled` is for the modals, which should not fetch until they open. Passing
 * `false` keeps whatever was loaded — a closed modal's list is still what it
 * will show when reopened — and simply stops refetching.
 *
 * Errors are swallowed to a `console.warn` and leave the previous list in place,
 * matching what every call site did before: this is a picker's option list, and
 * an empty dropdown is a worse failure than a slightly stale one.
 */
export function useManufacturerCatalogue(opts?: { enabled?: boolean }): {
  manufacturers: ManufacturerCatalogueEntry[];
  loading: boolean;
  /** Force a refetch — for a surface with its own Refresh control. */
  reload: () => void;
} {
  const enabled = opts?.enabled ?? true;
  const [manufacturers, setManufacturers] = useState<ManufacturerCatalogueEntry[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  /**
   * Bumped by the subscription and by `reload`. Separate from the data so a
   * refetch is expressible without clearing what is on screen — the list stays
   * rendered while the new one loads.
   */
  const [refetchToken, setRefetchToken] = useState(0);
  const [loadedToken, setLoadedToken] = useState(-1);

  const reload = useCallback(() => setRefetchToken((n) => n + 1), []);

  useEffect(() => subscribeManufacturerCatalogue(reload), [reload]);

  useEffect(() => {
    // Nothing to do if this token's fetch already landed — that is the cheap
    // half of the old `manufacturers.length > 0` guard, minus its blindness to
    // later changes.
    if (!enabled || loadedToken === refetchToken) return;
    let cancelled = false;
    setLoading(true);
    getManufacturerCatalogue()
      .then((list) => {
        if (cancelled) return;
        setManufacturers(list);
        setLoadedToken(refetchToken);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[manufacturers] catalogue load failed', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, refetchToken, loadedToken]);

  return { manufacturers, loading, reload };
}

/**
 * Run `onChange` when a manufacturer is created or deleted anywhere in the app.
 *
 * For the surfaces that do NOT hold a plain catalogue list: the ones that load
 * it inside a wider `Promise.all` refresh, or lazily when a picker opens. They
 * keep their own loader and just get told when to run it.
 *
 * `onChange` is read through a ref, so an inline arrow does not resubscribe on
 * every render.
 */
export function useManufacturerCatalogueChanged(onChange: () => void): void {
  const handler = useRef(onChange);
  handler.current = onChange;
  useEffect(
    () => subscribeManufacturerCatalogue(() => handler.current()),
    [],
  );
}
